/**
 * L5: lock state machine and auto-lock on exit (1Password Settings > Security, "Auto-lock").
 *
 * The web has no device-lock event (01 §3), so the lock works from two timestamps: when the page
 * hides and when it comes back. "Immediately" locks as the page hides, which also means the app
 * switcher snapshot shows the lock screen. All functions are pure and return a new state.
 *
 * Each Face ID prompt has an id (CSO gate on PR #100). The caller issues it, never reusing one,
 * and an answer applies only when it names the prompt that is open now. An answer for a prompt
 * that a hide, a lock or a newer prompt has closed changes nothing.
 */
import type { AutoLockSetting, LockPhase, UnlockResult } from "../contract.js";

export interface LockState {
  phase: LockPhase;
  unlockedAt: number | null;
  hiddenAt: number | null;
  /** The open Face ID prompt's id while prompting, else null. */
  prompt: number | null;
}

export const AUTO_LOCK_OPTIONS: AutoLockSetting[] = [0, 60, 300, 600];

export function autoLockLabel(s: AutoLockSetting): string {
  switch (s) {
    case 0:
      return "Immediately";
    case 60:
      return "After 1 minute";
    case 300:
      return "After 5 minutes";
    case 600:
      return "After 10 minutes";
  }
}

export function initialLock(): LockState {
  return { phase: "locked", unlockedAt: null, hiddenAt: null, prompt: null };
}

/**
 * The Face ID tap opens prompt `prompt`. Only from a locked phase: a second tap while prompting
 * changes nothing, so the first prompt stays the open one.
 */
export function beginUnlock(s: LockState, prompt: number): LockState {
  if (s.phase === "locked" || s.phase === "cancelled" || s.phase === "unavailable") return { ...s, phase: "prompting", prompt };
  return s;
}

/**
 * The answer to prompt `prompt`. Applies only while that prompt is the open one, so an answer
 * that lands after a lock, after a hide under "Immediately", or after a newer prompt opened can
 * never unlock or fail the vault. There is no password fallback: a failure stays locked.
 * An answer that lands while the page is hidden keeps the hide time, so auto-lock on return
 * counts from when the page hid, not from when the answer came.
 */
export function finishUnlock(s: LockState, prompt: number, result: UnlockResult, now: number): LockState {
  if (s.phase !== "prompting" || s.prompt !== prompt) return s;
  if (result.ok) return { phase: "unlocked", unlockedAt: now, hiddenAt: s.hiddenAt, prompt: null };
  return { phase: result.reason === "unavailable" ? "unavailable" : "cancelled", unlockedAt: null, hiddenAt: null, prompt: null };
}

export function lockNow(_s: LockState): LockState {
  return initialLock();
}

/**
 * The page hid. Unlocked: "Immediately" locks now, a longer setting stamps the time. Prompting
 * (CSO review of PR #95): the answer may still arrive while the page is away, so "Immediately"
 * closes the prompt and locks (`finishUnlock` then ignores its answer), and a longer setting
 * keeps the prompt open and stamps the time its answer will inherit.
 */
export function onHidden(s: LockState, now: number, autoLock: AutoLockSetting): LockState {
  if (s.phase !== "unlocked" && s.phase !== "prompting") return s;
  if (autoLock === 0) return initialLock();
  return { ...s, hiddenAt: now };
}

export function onVisible(s: LockState, now: number, autoLock: AutoLockSetting): LockState {
  if (s.phase !== "unlocked" || s.hiddenAt === null) return { ...s, hiddenAt: null };
  if (now - s.hiddenAt >= autoLock * 1000) return initialLock();
  return { ...s, hiddenAt: null };
}
